import React, { useCallback, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { FeedbackCommentDialog } from './FeedbackCommentDialog';
import './RatingButtons.css';

interface RatingButtonsProps {
  sessionId: string;
  messageId: string;
  currentRating?: 'up' | 'down';
  onRate: (rating: 'up' | 'down', comment?: string) => void;
  disabled?: boolean;
}

export const RatingButtons: React.FC<RatingButtonsProps> = React.memo(({
  sessionId,
  messageId,
  currentRating,
  onRate,
  disabled = false
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingRating, setPendingRating] = useState<'up' | 'down' | null>(null);

  const handleThumbUp = useCallback(() => {
    if (currentRating) return;
    onRate('up');
  }, [currentRating, onRate]);

  const handleThumbDown = useCallback(() => {
    if (currentRating) return;
    setPendingRating('down');
    setDialogOpen(true);
  }, [currentRating]);

  const handleDialogSubmit = useCallback((comment?: string) => {
    if (pendingRating) {
      onRate(pendingRating, comment);
    }
    setDialogOpen(false);
    setPendingRating(null);
  }, [pendingRating, onRate]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setPendingRating(null);
  }, []);

  const isRated = !!currentRating;

  return (
    <div className="rating-buttons">
      <Tooltip title={isRated ? "Bereits bewertet" : "Hilfreich"} arrow>
        <span>
          <IconButton
            className={`rating-btn ${currentRating === 'up' ? 'active up' : ''}`}
            onClick={handleThumbUp}
            disabled={disabled || isRated}
            size="small"
            aria-label="Hilfreich"
          >
            {currentRating === 'up' ? (
              <ThumbUpIcon fontSize="small" />
            ) : (
              <ThumbUpOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title={isRated ? "Bereits bewertet" : "Nicht hilfreich"} arrow>
        <span>
          <IconButton
            className={`rating-btn ${currentRating === 'down' ? 'active down' : ''}`}
            onClick={handleThumbDown}
            disabled={disabled || isRated}
            size="small"
            aria-label="Nicht hilfreich"
          >
            {currentRating === 'down' ? (
              <ThumbDownIcon fontSize="small" />
            ) : (
              <ThumbDownOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <FeedbackCommentDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
      />
    </div>
  );
});

export default RatingButtons;
